import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import { Edit3, BookOpen, Layers, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "../../services/api";
import { BlogPost } from "../../global-components/src/types";
import { useAuth } from "../../global-components/src";
import BlogNavbar from "./components/BlogNavbar";
import BlogCard from "./components/BlogCard";
import "./style.css";

const BlogHome: React.FC = () => {
    const { t } = useTranslation();
    const siteUrl = "https://pacoca.net";
    const pageUrl = `${siteUrl}/blog`;
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();

    const [communityPosts, setCommunityPosts] = useState<BlogPost[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    useEffect(() => {
        fetchCommunityPosts(page, search);
    }, [page]);

    // Debounced search
    useEffect(() => {
        const handler = setTimeout(() => {
            setPage(1);
            fetchCommunityPosts(1, search);
        }, 400);

        return () => clearTimeout(handler);
    }, [search]);

    const fetchCommunityPosts = async (currentPage: number, searchTerm: string) => {
        try {
            setLoading(true);
            const params: any = { page: currentPage, limit: 9 };
            if (searchTerm.trim()) {
                params.search = searchTerm.trim();
            }
            const res = await api().get("/blog", { params });
            setCommunityPosts(res.data?.data || []);
            setTotalPages(res.data?.last_page || 1);
        } catch (err) {
            console.error("Erro ao buscar artigos do blog:", err);
        } finally {
            setLoading(false);
        }
    };

    const structuredData = {
        "@context": "https://schema.org",
        "@type": "Blog",
        name: "Blog do Paçoca",
        description: "Histórias, ideias, tutoriais e tecnologia na rede social brasileira Paçoca.",
        url: pageUrl,
        publisher: { "@type": "Organization", name: "Paçoca Inc.", url: siteUrl },
    };

    return (
        <>
            <Helmet>
                <title>{t("blog.homeTitle")}</title>
                <meta
                    name="description"
                    content={t("blog.homeDescription")}
                />
                <meta name="keywords" content="blog paçoca, rede social brasileira, artigos, tutoriais, tecnologia" />
                <link rel="canonical" href={pageUrl} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={pageUrl} />
                <meta property="og:title" content={t("blog.homeTitle")} />
                <meta property="og:description" content={t("blog.homeDescription")} />
                <meta property="og:image" content={`${siteUrl}/img/link-preview.png?v=1`} />
                <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
            </Helmet>

            <div className="blog-page">
                <BlogNavbar search={search} onSearchChange={setSearch} showSearch={true} />

                {/* Hero section */}
                <header className="blog-hero">
                    <div className="blog-hero-inner">
                        <div className="blog-hero-badge-wrap">
                            <span className="blog-eyebrow">{t("blog.eyebrow")}</span>
                        </div>
                        <h1 className="blog-hero-title">{t("blog.heroTitle")}</h1>
                        <p className="blog-hero-sub">{t("blog.heroSub")}</p>

                        <div className="blog-hero-actions">
                            <button
                                type="button"
                                onClick={() => navigate(isAuthenticated ? "/blog/escrever" : "/login")}
                                className="blog-hero-write-btn"
                            >
                                <Edit3 size={16} />
                                <span>{t("blog.writeAnArticle")}</span>
                            </button>
                            {isAuthenticated && (
                                <Link to="/blog/gerenciar" className="blog-hero-manage-btn">
                                    <Layers size={16} />
                                    <span>{t("blog.myArticles")}</span>
                                </Link>
                            )}
                        </div>
                    </div>
                </header>

                <main className="blog-feed-container">
                    {/* Search bar on mobile */}
                    <div className="blog-mobile-search d-sm-none">
                        <div className="blog-search-box-wrap">
                            <Search size={16} className="search-icon" />
                            <input
                                type="text"
                                placeholder={t("blog.searchPlaceholder")}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="blog-search-box"
                            />
                        </div>
                    </div>

                    {loading ? (
                        <div className="blog-feed-loading">
                            <p>{t("blog.loading")}</p>
                        </div>
                    ) : communityPosts.length === 0 ? (
                        <div className="blog-feed-empty">
                            <BookOpen size={48} className="empty-icon" />
                            <h3>{t("blog.emptyTitle")}</h3>
                            <p>{t("blog.emptyDesc")}</p>
                            <button
                                type="button"
                                onClick={() => navigate(isAuthenticated ? "/blog/escrever" : "/login")}
                                className="blog-hero-write-btn mt-3"
                            >
                                <Edit3 size={16} />
                                <span>{t("blog.createFirst")}</span>
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="blog-feed-grid">
                                {communityPosts.map((post, idx) => (
                                    <BlogCard key={post.id} post={post} featured={idx === 0} />
                                ))}
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="blog-pagination">
                                    <button
                                        type="button"
                                        disabled={page <= 1}
                                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                                        className="pagination-btn"
                                    >
                                        {t("blog.previous")}
                                    </button>
                                    <span className="pagination-info">
                                        {t("blog.pageOf", { current: page, total: totalPages })}
                                    </span>
                                    <button
                                        type="button"
                                        disabled={page >= totalPages}
                                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                        className="pagination-btn"
                                    >
                                        {t("blog.next")}
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </main>
            </div>
        </>
    );
};

export default BlogHome;
